import { useEffect, useMemo, useState } from 'react';
import { RotateCcwIcon, SaveIcon, SendIcon } from 'lucide-react';
import { findTelegramHtmlProblems, type NotificationTemplate } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { EmptyState, ErrorNotice, SuccessNotice, TableSkeleton } from '@/components/states';
import { TelegramHtmlEditor } from '@/components/telegram-html-editor';
import { TelegramHtmlPreview } from '@/components/telegram-html-preview';
import {
  useNotificationTemplates,
  usePreviewNotificationTemplate,
  useResetNotificationTemplate,
  useSendTestNotification,
  useSetNotificationTemplate,
} from '@/lib/admin-queries';
import { strings } from '@/i18n/strings';

interface Draft {
  body: string;
  buttonLabel: string;
  buttonUrl: string;
}

function draftOf(template: NotificationTemplate): Draft {
  return {
    body: template.body,
    buttonLabel: template.buttonLabel ?? '',
    buttonUrl: template.buttonUrl ?? '',
  };
}

/**
 * Bot xabarlari (§3.8.3).
 *
 * Ilgari bu matnlar faqat `config/notification-templates.json` da edi — bitta so'zni tuzatish
 * uchun faylni konteynerga ulab, restart qilish kerak bo'lardi, ya'ni amalda deploy. Bu sahifa
 * o'sha matnni bazaga yozadi va u shu zahoti kuchga kiradi; fayl va koddagi standart pastdagi
 * zaxira qatlam bo'lib qoladi.
 */
export function NotificationTemplatesPage() {
  const templates = useNotificationTemplates();
  const save = useSetNotificationTemplate();
  const reset = useResetNotificationTemplate();
  const preview = usePreviewNotificationTemplate();
  const sendTest = useSendTestNotification();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [chatId, setChatId] = useState('');

  // `useMemo` shart: `?? []` har renderda YANGI massiv yasaydi va quyidagi effekt uni
  // o'zgargan deb ko'rib, cheksiz qayta ishga tushardi.
  const items = useMemo(() => templates.data?.items ?? [], [templates.data]);
  const selected = items.find((item) => item.key === selectedKey) ?? null;

  // Ro'yxat kelgach birinchisi ochiladi: bo'sh o'ng panel sahifani "ishlamayaptimi?" qilib
  // ko'rsatardi, holbuki tanlanadigan narsa allaqachon ekranda.
  useEffect(() => {
    if (!selectedKey && items.length > 0) {
      setSelectedKey(items[0]!.key);
      setDraft(draftOf(items[0]!));
    }
  }, [items, selectedKey]);

  function select(template: NotificationTemplate) {
    setSelectedKey(template.key);
    setDraft(draftOf(template));
    setNotice(null);
    preview.reset();
    sendTest.reset();
  }

  /**
   * Tugma ikkala maydon bilan birga ma'noga ega — server ham shuni majburlaydi. Bu yerda esa
   * so'rov YUBORILMASDAN oldin aytiladi: 400 ni forma xatosiga aylantirish shu bitta qoida
   * uchun ortiqcha yo'l.
   */
  const buttonHalfFilled = draft ? Boolean(draft.buttonLabel) !== Boolean(draft.buttonUrl) : false;
  const htmlProblems = draft ? findTelegramHtmlProblems(draft.body) : [];
  const blocked = !draft || draft.body.trim() === '' || buttonHalfFilled || htmlProblems.length > 0;

  function requestBody(current: Draft) {
    return {
      body: current.body,
      buttonLabel: current.buttonLabel ? current.buttonLabel : null,
      buttonUrl: current.buttonUrl ? current.buttonUrl : null,
    };
  }

  if (templates.isLoading) return <TableSkeleton rows={6} columns={3} />;
  if (templates.isError) return <ErrorNotice error={templates.error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={strings.notificationTemplates.title}
        description={strings.notificationTemplates.subtitle}
      />
      <p className="text-sm text-muted-foreground">{strings.notificationTemplates.hint}</p>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{strings.notificationTemplates.listHeading}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {items.length === 0 && <EmptyState title={strings.notificationTemplates.empty} />}
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => select(item)}
                aria-current={item.key === selectedKey}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                  item.key === selectedKey ? 'bg-muted font-medium' : 'hover:bg-muted/60'
                }`}
              >
                <span className="truncate font-mono text-xs">{item.key}</span>
                {item.overridden && (
                  <Badge variant="secondary">{strings.notificationTemplates.overridden}</Badge>
                )}
              </button>
            ))}
          </CardContent>
        </Card>

        {!selected || !draft ? (
          <EmptyState title={strings.notificationTemplates.selectHint} />
        ) : (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-base">{selected.key}</CardTitle>
                <CardDescription>
                  {selected.overridden && selected.updatedByEmail
                    ? `${strings.notificationTemplates.columnUpdated}: ${selected.updatedByEmail}`
                    : strings.notificationTemplates.default}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/*
                  `Field` EMAS, ochiq `htmlFor`: `Field` bolalarini `<label>` ichiga o'raydi, va
                  muharrirda matn maydonidan oldin format tugmalari turadi — o'ralgan yorliq esa
                  o'zidan keyingi BIRINCHI nazoratga bog'lanadi, ya'ni yorliq matn maydoniga
                  emas, «Qalin» tugmasiga tegishli bo'lib qolardi.
                */}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <label htmlFor="template-body" className="text-sm leading-none font-medium">
                    {strings.notificationTemplates.bodyLabel}
                  </label>
                  <TelegramHtmlEditor
                    id="template-body"
                    value={draft.body}
                    onChange={(body) => setDraft({ ...draft, body })}
                    placeholders={selected.placeholders}
                  />
                </div>

                {htmlProblems.length > 0 && (
                  <p role="alert" className="text-sm text-destructive">
                    {htmlProblems.map((problem) => `<${problem.tag}>`).join(', ')}
                  </p>
                )}

                <div className="space-y-2 rounded-md border p-3">
                  <div className="text-sm font-medium">
                    {strings.notificationTemplates.buttonHeading}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {strings.notificationTemplates.buttonHint}
                  </p>
                  <Field label={strings.notificationTemplates.buttonLabelLabel}>
                    <Input
                      id="template-button-label"
                      value={draft.buttonLabel}
                      onChange={(event) => setDraft({ ...draft, buttonLabel: event.target.value })}
                    />
                  </Field>
                  <Field label={strings.notificationTemplates.buttonUrlLabel}>
                    <Input
                      id="template-button-url"
                      className="font-mono text-sm"
                      value={draft.buttonUrl}
                      onChange={(event) => setDraft({ ...draft, buttonUrl: event.target.value })}
                    />
                  </Field>
                  {buttonHalfFilled && (
                    <p role="alert" className="text-sm text-destructive">
                      {strings.notificationTemplates.buttonHint}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    disabled={blocked || save.isPending}
                    onClick={async () => {
                      await save.mutateAsync({ key: selected.key, ...requestBody(draft) });
                      setNotice(strings.notificationTemplates.saved);
                    }}
                  >
                    <SaveIcon className="size-4" aria-hidden />
                    {save.isPending
                      ? strings.notificationTemplates.saving
                      : strings.notificationTemplates.save}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    disabled={!selected.overridden || reset.isPending}
                    onClick={async () => {
                      if (!window.confirm(strings.notificationTemplates.resetConfirm)) return;
                      const restored = await reset.mutateAsync(selected.key);
                      setDraft(draftOf(restored));
                      setNotice(strings.notificationTemplates.resetDone);
                    }}
                  >
                    <RotateCcwIcon className="size-4" aria-hidden />
                    {reset.isPending
                      ? strings.notificationTemplates.resetting
                      : strings.notificationTemplates.reset}
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    disabled={blocked || preview.isPending}
                    onClick={() => preview.mutate({ key: selected.key, ...requestBody(draft) })}
                  >
                    {preview.isPending
                      ? strings.notificationTemplates.previewing
                      : strings.notificationTemplates.previewButton}
                  </Button>
                </div>

                {notice && (
                  <p role="status" className="text-sm text-muted-foreground">
                    {notice}
                  </p>
                )}
                {save.isError && <ErrorNotice error={save.error} />}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{strings.notificationTemplates.previewHeading}</CardTitle>
                <CardDescription>{strings.notificationTemplates.previewHint}</CardDescription>
              </CardHeader>
              <CardContent>
                {preview.data ? (
                  <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <TelegramHtmlPreview html={preview.data.text} />
                    {preview.data.buttonLabel && (
                      <div className="rounded-md border bg-background px-3 py-2 text-center text-sm font-medium">
                        {preview.data.buttonLabel}
                        <div className="mt-0.5 break-all font-mono text-[11px] font-normal text-muted-foreground">
                          {preview.data.buttonUrl}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {strings.notificationTemplates.previewEmpty}
                  </p>
                )}
                {preview.isError && <ErrorNotice error={preview.error} />}
              </CardContent>
            </Card>

            {/*
              Oldindan ko'rishdan KEYIN turadi va bu tartib ataylab: birinchisi bepul va
              darhol, ikkinchisi esa haqiqiy botdan haqiqiy chatga xabar yuboradi. Ustma-ust
              qo'yilsa, "ko'rish" niyatidagi bosish yuborishga tushib ketardi.
            */}
            <Card>
              <CardHeader>
                <CardTitle>{strings.notificationTemplates.testHeading}</CardTitle>
                <CardDescription>{strings.notificationTemplates.testHint}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="flex flex-wrap items-end gap-2">
                  <Field label={strings.notificationTemplates.testChatId} className="w-56">
                    <Input
                      inputMode="numeric"
                      placeholder="123456789"
                      value={chatId}
                      onChange={(event) => setChatId(event.target.value)}
                    />
                  </Field>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={blocked || chatId.trim() === '' || sendTest.isPending}
                    onClick={() =>
                      sendTest.mutate({
                        key: selected.key,
                        chatId: chatId.trim(),
                        // Qoralama YUBORILADI: saqlash shart emas, aks holda har bir sinov
                        // jonli matnni almashtirardi.
                        ...requestBody(draft),
                      })
                    }
                  >
                    <SendIcon className="size-4" aria-hidden />
                    {sendTest.isPending
                      ? strings.notificationTemplates.testSending
                      : strings.notificationTemplates.testSend}
                  </Button>
                </div>

                {sendTest.isError && <ErrorNotice error={sendTest.error} />}
                {sendTest.isSuccess && !sendTest.data.ok && (
                  <ErrorNotice>
                    {strings.notificationTemplates.testFailed.replace(
                      '{error}',
                      sendTest.data.error ?? '',
                    )}
                  </ErrorNotice>
                )}
                {sendTest.isSuccess && sendTest.data.ok && (
                  <SuccessNotice>
                    {sendTest.data.hasButton
                      ? strings.notificationTemplates.testSent
                      : strings.notificationTemplates.testNoButton}
                  </SuccessNotice>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
